import React, { useState, useEffect } from 'react';
import {
    connectThermalPrinter,
    disconnectThermalPrinter,
    testThermalPrinter,
    openCashDrawer,
    getPrinterStatus,
    PRINTER_MODES
} from '../utils/receiptPrinter';
import { useLanguage } from '../contexts/LanguageContext';

const PrinterSettings = ({ onClose }) => {
    const { t } = useLanguage();
    const [printerStatus, setPrinterStatus] = useState({
        supported: false,
        connected: false,
        browserSupported: true
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [preferredMode, setPreferredMode] = useState(PRINTER_MODES.AUTO);

    useEffect(() => {
        updatePrinterStatus();
        
        // Load preferred mode from localStorage
        const savedMode = localStorage.getItem('printerMode');
        if (savedMode && Object.values(PRINTER_MODES).includes(savedMode)) {
            setPreferredMode(savedMode);
        }
    }, []);

    const updatePrinterStatus = () => {
        setPrinterStatus(getPrinterStatus());
    };

    const showMessage = (msg, isError = false) => {
        setMessage(msg);
        setTimeout(() => setMessage(''), 3000);
    };

    const handleConnect = async () => {
        setLoading(true);
        try {
            const result = await connectThermalPrinter();
            if (result.success) {
                showMessage(result.message);
                updatePrinterStatus();
            } else {
                showMessage(result.message, true);
            }
        } catch (error) {
            showMessage(t('errorConnectingPrinter') + ': ' + error.message, true);
        }
        setLoading(false);
    };

    const handleDisconnect = async () => {
        setLoading(true);
        try {
            const result = await disconnectThermalPrinter();
            if (result.success) {
                showMessage(result.message);
                updatePrinterStatus();
            } else {
                showMessage(result.message, true);
            }
        } catch (error) {
            showMessage(t('errorDisconnectingPrinter') + ': ' + error.message, true);
        }
        setLoading(false);
    };

    const handleTest = async () => {
        setLoading(true);
        try {
            const result = await testThermalPrinter();
            if (result.success) {
                showMessage(result.message);
            } else {
                showMessage(result.message, true);
            }
        } catch (error) {
            showMessage(t('errorInTest') + ': ' + error.message, true);
        }
        setLoading(false);
    };

    const handleOpenDrawer = async () => {
        setLoading(true);
        try {
            const result = await openCashDrawer();
            if (result.success) {
                showMessage(result.message);
            } else {
                showMessage(result.message, true);
            }
        } catch (error) {
            showMessage(t('errorOpeningDrawer') + ': ' + error.message, true);
        }
        setLoading(false);
    };

    const handleModeChange = (mode) => {
        setPreferredMode(mode);
        localStorage.setItem('printerMode', mode);
        showMessage(t('printModeChangedTo') + `: ${mode}`);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96 max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">{t('printerSettings')}</h2>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                    >
                        ×
                    </button>
                </div>

                {/* Status Display */}
                <div className="mb-4 p-3 bg-gray-100 rounded">
                    <h3 className="font-semibold mb-2">{t('status')}:</h3>
                    <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span>{t('webSerialApi')}:</span>
                            <span className={printerStatus.supported ? 'text-green-600' : 'text-red-600'}>
                                {printerStatus.supported ? t('compatible') : t('notCompatible')}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>{t('thermalPrinter')}:</span>
                            <span className={printerStatus.connected ? 'text-green-600' : 'text-gray-600'}>
                                {printerStatus.connected ? t('connected') : t('disconnected')}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>{t('browserPrinting')}:</span>
                            <span className={printerStatus.browserSupported ? 'text-green-600' : 'text-red-600'}>
                                {printerStatus.browserSupported ? t('available') : t('notAvailable')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Printer Mode Selection */}
                <div className="mb-4">
                    <h3 className="font-semibold mb-2">{t('printMode')}:</h3>
                    <div className="space-y-2">
                        {Object.entries(PRINTER_MODES).map(([key, value]) => (
                            <label key={key} className="flex items-center">
                                <input
                                    type="radio"
                                    name="printerMode"
                                    value={value}
                                    checked={preferredMode === value}
                                    onChange={(e) => handleModeChange(e.target.value)}
                                    className="mr-2"
                                />
                                <span className="capitalize">
                                    {value === 'auto' ? t('automaticThermalToBrowser') :
                                     value === 'thermal' ? t('thermalPrinterOnly') :
                                     t('browserOnly')}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Connection Controls */}
                {printerStatus.supported && (
                    <div className="mb-4 space-y-2">
                        <h3 className="font-semibold">{t('thermalPrinterControls')}:</h3>
                        
                        <div className="flex space-x-2">
                            {!printerStatus.connected ? (
                                <button
                                    onClick={handleConnect}
                                    disabled={loading}
                                    className="flex-1 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                                >
                                    {loading ? t('connecting') : t('connectPrinter')}
                                </button>
                            ) : (
                                <button
                                    onClick={handleDisconnect}
                                    disabled={loading}
                                    className="flex-1 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 disabled:opacity-50"
                                >
                                    {loading ? t('disconnecting') : t('disconnect')}
                                </button>
                            )}
                        </div>

                        {printerStatus.connected && (
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleTest}
                                    disabled={loading}
                                    className="flex-1 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
                                >
                                    {loading ? t('printing') : t('printTest')}
                                </button>
                                <button
                                    onClick={handleOpenDrawer}
                                    disabled={loading}
                                    className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:opacity-50"
                                >
                                    {loading ? t('opening') : t('openDrawer')}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Browser compatibility warning */}
                {!printerStatus.supported && (
                    <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded">
                        <p className="text-sm text-yellow-700">
                            <strong>{t('webSerialApiNotCompatible')}.</strong><br/>
                            {t('useChromeEdgeForThermalPrinters')}.
                            {t('browserPrintingWillStillWork')}.
                        </p>
                    </div>
                )}

                {/* Message Display */}
                {message && (
                    <div className={`mb-4 p-3 rounded ${
                        message.includes('Error') || message.includes('error') 
                            ? 'bg-red-100 text-red-700 border border-red-400' 
                            : 'bg-green-100 text-green-700 border border-green-400'
                    }`}>
                        {message}
                    </div>
                )}

                {/* Instructions */}
                <div className="text-xs text-gray-600 space-y-1">
                    <p><strong>{t('instructions')}:</strong></p>
                    <ul className="list-disc list-inside space-y-1">
                        <li>{t('connectThermalPrinterUsb')}</li>
                        <li>{t('clickConnectPrinterAndSelectPort')}</li>
                        <li>{t('usePrintTestToVerifyConnection')}</li>
                        <li>{t('receiptsWillPrintAutomatically')}</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default PrinterSettings;